import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { blurbDefault, clean, stagger } from '../utils/quinn';
import { useRawProperty } from '../utils/hooks';
import { byDist, miles, type Poi } from '../utils/places';
import { PATHS } from '../utils/site';

const ctaDef: SlotGroupDefinition = {
  label: 'Neighborhood Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Explore the Neighborhood' },
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const photoDef: SlotGroupDefinition = {
  label: 'Photo',
  description: 'Until set, the closest park with a photo is shown, captioned with its distance.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[0].poi_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'neighborhood_pois[0].poi_name' },
    caption: { label: 'Caption', type: 'text', defaultField: 'neighborhood_pois[0].poi_name' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 0,
    fieldMappings: { image: 'poi_featured_image', alt: 'poi_name', caption: 'poi_name' },
  },
  generator: {
    prompt: 'A nearby place that shows the best of the neighborhood, with a real photo: a park, trail or green space close to the property if there is one. Caption it with the place name.',
    context: 'Tall photo beside the schools / parks / shopping lists in the Neighborhood section of the home page. Caption is a short pill over the photo.',
  },
};

const GROUPS: Array<[string, string, string]> = [
  ['Education', 'Schools', '#schools'],
  ['Nature & Outdoor Attractions', 'Parks & outdoors', '#around-parks'],
  ['Shopping & Retail', 'Shopping & errands', '#around-shopping'],
];

export function NeighborhoodCallout() {
  const p = useRawProperty();
  const cta = useSlotGroup('neighborhoodcallout.cta', ctaDef);
  const photoSlot = useSlotGroup('neighborhoodcallout.photo', photoDef);
  const pois = useCollectionBinding<Poi>('neighborhoodcallout.places', { collection: 'neighborhood_pois' });
  const rawPois = (p.neighborhood_pois as Poi[]) || [];

  const titleDefault = blurbDefault(p.property_home_neighborhood_blurb, 'title');
  const leadDefault = blurbDefault(p.property_home_neighborhood_blurb, 'body');
  const groups = GROUPS.map(([type, label, hash]) => ({ type, label, hash, list: pois.filter((x) => x.poi_type === type).sort(byDist).slice(0, 3) })).filter((g) => g.list.length);
  const park =
    pois
      .filter((x) => x.poi_type === 'Nature & Outdoor Attractions' && clean(x.poi_featured_image))
      .sort(byDist)
      .find((x) => /park/i.test(x.poi_name || '')) || pois.find((x) => clean(x.poi_featured_image));
  const chosen = photoSlot.image && photoSlot.image !== clean(rawPois[0]?.poi_featured_image);
  const photo = chosen
    ? { src: photoSlot.image!, alt: photoSlot.alt || '', caption: photoSlot.caption || '' }
    : park
      ? { src: park.poi_featured_image!, alt: park.poi_name || '', caption: `${park.poi_name}${miles(park) != null ? ` · ${park.poi_miles_from_property}` : ''}` }
      : null;

  if (!groups.length) return null;

  return (
    <section className="section section--surface hood" id="neighborhood">
      <div className="section__head" data-reveal>
        <div className="section__lead-block">
          <RichTextContent path="neighborhoodcallout.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="neighborhoodcallout.title" as="h2" className="h2" defaultValue={titleDefault} />
        </div>
        <RichTextContent path="neighborhoodcallout.lead" as="p" className="section__lead" defaultValue={leadDefault} />
      </div>
      <div className="flex items-stretch gap-16 max-[1199px]:flex-col">
        <EditableCollectionBinding bindingPath="neighborhoodcallout.places" label="Places" className="flex grow flex-col gap-9">
          {groups.map((g, gi) => (
            <div className="flex flex-col" key={g.type} data-reveal style={stagger(gi)}>
              <a
                className="eyebrow border-b border-b-[--q-secondary] pb-3 max-[767px]:flex max-[767px]:min-h-11 max-[767px]:items-end min-[768px]:max-[1099px]:flex min-[768px]:max-[1099px]:min-h-11 min-[768px]:max-[1099px]:items-end"
                href={`${PATHS.neighborhood}${g.hash}`}
              >
                {g.label} <span className="btn__arrow">→</span>
              </a>
              {g.list.map((x) => (
                <div
                  className="flex items-baseline gap-6 border-b border-b-[--q-border] py-3.5 font-heading text-[22px] leading-7 max-[767px]:py-3 max-[767px]:text-[18px] max-[767px]:leading-6"
                  key={x.poi_name}
                >
                  <span className="grow">{x.poi_name}</span>
                  <span className="w-[120px] shrink-0 text-right font-body text-sm leading-5 text-[--q-muted] max-[767px]:w-[92px] max-[767px]:text-[13px]">
                    {x.poi_miles_from_property || ''}
                    {x.poi_driving_minutes && g.type !== 'Shopping & Retail' ? ` · ${x.poi_driving_minutes.replace('mins', 'min')}` : ''}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </EditableCollectionBinding>
        <EditableSlotGroup groupPath="neighborhoodcallout.photo" definition={photoDef} className={photo ? 'photo photo--tall max-[767px]:order-[-1]' : ''}>
          {photo && (
            <>
              <VibesitesImage url={photo.src} alt={photo.alt} sizes="(min-width: 1200px) 592px, 100vw" />
              {photo.caption && <div className="pill pill--paper photo__caption">{photo.caption}</div>}
            </>
          )}
        </EditableSlotGroup>
      </div>
      <div className="section__cta" data-reveal>
        <div className="section__buttons">
          <EditableSlotGroup groupPath="neighborhoodcallout.cta" definition={ctaDef}>
            <a className="btn btn-primary" href={cta.url || PATHS.neighborhood} target={cta._target || '_self'}>
              {cta.label || 'Explore the Neighborhood'} <span className="btn__arrow">→</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodCallout',
  displayName: 'Neighborhood Callout',
  tokens: {
    'neighborhoodcallout.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue:
        '<p>Neighborhood · <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'neighborhoodcallout.title': {
      label: 'Title',
      description: 'Until edited, shows the bold first sentence of the home neighborhood blurb.',
      type: 'richtext',
      defaultValue: '<p>Everything you need, close by</p>',
      suggestedFields: ['property_home_neighborhood_blurb', 'property_neighborhood_blurb'],
      generator: {
        prompt: "A short, hyperlocal headline about this property's neighborhood. No closing period.",
        context: 'H2 of the Neighborhood section on the home page, above the lead and the nearby schools / parks / shopping lists.',
        length: '3–8 words',
      },
    },
    'neighborhoodcallout.lead': {
      label: 'Lead',
      description: 'Until edited, shows the home neighborhood blurb after its bold first sentence.',
      type: 'richtext',
      defaultValue: '<p></p>',
      suggestedFields: ['property_home_neighborhood_blurb', 'property_neighborhood_blurb'],
      generator: {
        prompt: 'A hyperlocal lead about everyday life around the property: real nearby roads, parks, shopping or employers. Point readers to the neighborhood page if it exists.',
        context: 'Lead under the Neighborhood headline on the home page. The lists beside it already name the closest places, so do not just repeat them.',
        length: '1–2 sentences',
      },
    },
  },
  slotGroups: {
    'neighborhoodcallout.photo': photoDef,
    'neighborhoodcallout.cta': ctaDef,
  },
  collectionBindings: {
    'neighborhoodcallout.places': {
      label: 'Places',
      collection: 'neighborhood_pois',
    },
  },
};

(NeighborhoodCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodCallout;
